import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { FlatList, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { t } from 'ttag'

import { useAppDispatch, useAppSelector } from '../hooks'
import { useAppTheme } from '#app/theme'
import { ScreenProps } from '#app/types'
import { selectCan } from '#app/centraluser'
import WsActions from '../redux/ws'
import DiagnosticsActions, { computeStats, DiagnosticPing, selectSlavePings } from '../redux/diagnostics'

const PENDING_REFRESH_MS = 1000
const PENDING_TIMEOUT_MS = 30000

const formatLatency = (ms?: number): string => {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(2)} s`
}

const formatTime = (epoch: number): string => {
  const d = new Date(epoch)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const SlaveDiagnostics = ({ route: { params: { id } } }: ScreenProps<'SlaveDiagnostics'>) => {
  const theme = useAppTheme()
  const dispatch = useAppDispatch()
  const slaveId = Number(id)
  const can = useAppSelector(selectCan)
  const canUse = can('use')

  const pings = useAppSelector(state => selectSlavePings(state, slaveId))
  const hasPending = useMemo(() => pings.some(p => p.resolvedAt == null), [pings])

  // Force re-render every second while a ping is pending so the "elapsed" counter ticks
  // and so stale pings become visibly expired.
  const [, setTick] = React.useState(0)
  useEffect(() => {
    if (!hasPending) return
    const t = setInterval(() => setTick(n => n + 1), PENDING_REFRESH_MS)
    return () => clearInterval(t)
  }, [hasPending])

  const requestSeqRef = useRef(0)

  const sendCheck = useCallback(() => {
    if (!canUse) return
    requestSeqRef.current += 1
    const sentAt = Date.now()
    dispatch(DiagnosticsActions.diagnosticsCheckSent({
      id: `${slaveId}-${sentAt}-${requestSeqRef.current}`,
      slaveId,
      sentAt
    }))
    dispatch(WsActions.wsConnectionSendMessage({
      type: 'slave',
      id: slaveId,
      command: 'check'
    }))
  }, [canUse, dispatch, slaveId])

  const onClear = useCallback(() => {
    dispatch(DiagnosticsActions.diagnosticsClearSlave({ slaveId }))
  }, [dispatch, slaveId])

  const stats = useMemo(() => computeStats(pings), [pings])
  const reversed = useMemo(() => [...pings].reverse(), [pings])

  const renderRow = useCallback(({ item }: { item: DiagnosticPing }) => {
    const isPending = item.resolvedAt == null
    const elapsed = isPending ? Date.now() - item.sentAt : (item.latencyMs ?? 0)
    const color = isPending
      ? theme.colors.grey
      : item.kind === 'response' ? '#4CAF50' : '#ca6566'
    const iconName = isPending
      ? 'progress-clock'
      : item.kind === 'response' ? 'check-circle' : 'alert-circle'
    const label = isPending
      ? t`Waiting…`
      : item.kind === 'response' ? t`Response` : t`Timeout`

    return (
      <View style={[styles.row, { borderBottomColor: theme.colors.grey + '30' }]}>
        <MaterialCommunityIcons name={iconName} size={20} color={color} style={styles.rowIcon} />
        <View style={styles.rowMain}>
          <Text style={[styles.rowTime, { color: theme.textColor }]}>{formatTime(item.sentAt)}</Text>
          <Text style={[styles.rowLabel, { color }]}>{label}</Text>
        </View>
        <Text style={[styles.rowLatency, { color: theme.textColor }]}>
          {formatLatency(isPending ? elapsed : item.latencyMs)}
        </Text>
      </View>
    )
  }, [theme])

  return (
    <View style={[styles.container, { backgroundColor: theme.backgroundColor }]}>
      <View style={[styles.statsCard, { backgroundColor: theme.formForm?.search ?? '#1a1a1a' }]}>
        <View style={styles.statsRow}>
          <Stat label={t`Sent`} value={String(stats.total)} theme={theme} />
          <Stat label={t`OK`} value={String(stats.responses)} theme={theme} color="#4CAF50" />
          <Stat label={t`Timeout`} value={String(stats.timeouts)} theme={theme} color="#ca6566" />
          <Stat label={t`Pending`} value={String(stats.pending)} theme={theme} />
        </View>
        <View style={styles.statsRow}>
          <Stat label={t`Last`} value={formatLatency(stats.lastLatencyMs ?? undefined)} theme={theme} />
          <Stat label={t`Avg`} value={formatLatency(stats.avgLatencyMs ?? undefined)} theme={theme} />
          <Stat label={t`p95`} value={formatLatency(stats.p95LatencyMs ?? undefined)} theme={theme} />
          <Stat
            label={t`Success`}
            value={stats.responses + stats.timeouts > 0 ? `${Math.round(stats.successRate * 100)}%` : '—'}
            theme={theme}
          />
        </View>
      </View>

      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.sendButton, { backgroundColor: canUse ? '#7C3AED' : theme.colors.grey, opacity: canUse ? 1 : 0.5 }]}
          onPress={sendCheck}
          disabled={!canUse}
        >
          <MaterialCommunityIcons name="radar" size={18} color="#fff" />
          <Text style={styles.sendButtonText}>{t`Send check`}</Text>
        </TouchableOpacity>
        {pings.length > 0 && (
          <TouchableOpacity style={styles.clearButton} onPress={onClear}>
            <MaterialCommunityIcons name="delete-outline" size={18} color={theme.colors.grey} />
            <Text style={[styles.clearButtonText, { color: theme.colors.grey }]}>{t`Clear`}</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={[styles.historyHeader, { borderBottomColor: theme.colors.grey + '40' }]}>
        <Text style={[styles.historyHeaderText, { color: theme.colors.grey }]}>{t`History`}</Text>
        <Text style={[styles.historyHeaderHint, { color: theme.colors.grey }]}>
          {t`Times below ${Math.round(PENDING_TIMEOUT_MS / 1000)}s round-trip; otherwise marked as timeout.`}
        </Text>
      </View>

      <FlatList
        data={reversed}
        keyExtractor={item => item.id}
        renderItem={renderRow}
        ListEmptyComponent={
          <View style={styles.empty}>
            <MaterialCommunityIcons name="radar" size={48} color={theme.colors.grey} />
            <Text style={[styles.emptyText, { color: theme.colors.grey }]}>
              {t`No checks yet. Tap "Send check" to start.`}
            </Text>
          </View>
        }
      />
    </View>
  )
}

const Stat = ({ label, value, theme, color }: { label: string, value: string, theme: any, color?: string }) => (
  <View style={styles.stat}>
    <Text style={[styles.statValue, { color: color ?? theme.textColor }]}>{value}</Text>
    <Text style={[styles.statLabel, { color: theme.colors.grey }]}>{label}</Text>
  </View>
)

export default SlaveDiagnostics

const styles = StyleSheet.create({
  container: { flex: 1 },
  statsCard: {
    marginHorizontal: 12,
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    gap: 8
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around'
  },
  stat: { alignItems: 'center', flex: 1 },
  statValue: { fontSize: 18, fontWeight: '700' },
  statLabel: { fontSize: 11, marginTop: 2 },
  actions: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 12,
    alignItems: 'center'
  },
  sendButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    flex: 1
  },
  sendButtonText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  clearButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8
  },
  clearButtonText: { fontSize: 13 },
  historyHeader: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 6,
    borderBottomWidth: StyleSheet.hairlineWidth
  },
  historyHeaderText: { fontSize: 12, fontWeight: '600', textTransform: 'uppercase' },
  historyHeaderHint: { fontSize: 11, marginTop: 2 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth
  },
  rowIcon: { marginRight: 12 },
  rowMain: { flex: 1 },
  rowTime: { fontSize: 14, fontWeight: '500' },
  rowLabel: { fontSize: 12, marginTop: 2 },
  rowLatency: { fontSize: 14, fontWeight: '600', fontVariant: ['tabular-nums'] },
  empty: { alignItems: 'center', paddingVertical: 48, gap: 12 },
  emptyText: { fontSize: 14 }
})
